import cn from "classnames";
import ServiceWorksPlanning from "components/modal/serviceWorksPlanning";
import { useValue } from "components/planning/realtime";
import WithoutDriver from "components/shared/withoutDriver";
import { getNumberStructure, isValidCarNumber, isValidTrailerNumber } from "helpers/carnumber";
import { dateTimeToView } from "helpers/date";
import { bidStatusesEnum } from "helpers/enums";
import { formatNumber, truncateString, ymEvent } from "helpers/system";
import { lastNameWithInitials } from "helpers/user";
import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo, useState } from "react";
import { Popover } from "react-tiny-popover";
import CarDropd from "../../city/cell/car/dropd";

export default function GroupItem(props) {
    const {
        item,
        options,
        options: {
            trailerTypes,
            index,
            forwardingEnabled,
            organization,
            modal,
            redrawTimeline,
            showEstimationComment,
            timelineRef,
        },
    } = props;

    useEffect(() => {
        redrawTimeline();
        timelineRef.current.dom.container.addEventListener("group-options-changed", onOptionsChanged);
        return () => timelineRef.current.dom.container.removeEventListener("group-options-changed", onOptionsChanged);
    }, []);

    const onOptionsChanged = (e) => {
        setShowComment(e.detail.showEstimationComment);
        redrawTimeline();
    };

    const realtimeItem = useValue({
        listenEventNames: [
            ...(item.car?.id ? [`groups[${item.car?.id}]`, `groups[car.id(${item.car?.id})]`] : []),
            ...(item.driver?.id ? [`groups[driver.id(${item.driver?.id})]`] : []),
            ...(item.trailer?.id ? [`groups[trailer.id(${item.trailer?.id})]`] : []),
        ],
        originalValue: item,
    });

    const [openedDropd, setOpenedDropd] = useState(false);
    const [showComment, setShowComment] = useState(showEstimationComment);
    const openDropd = () => {
        if (openedDropd) {
            return;
        }
        setOpenedDropd(true);
        setTimeout(() => document.addEventListener("click", closeDropd), 0);
    };

    const closeDropd = () => {
        setOpenedDropd(false);
        document.removeEventListener("click", closeDropd);
    };

    const carNumber = getNumberStructure(realtimeItem.car.number);
    const trailerNumber = getNumberStructure(realtimeItem.trailer?.number);
    const { withoutBidPeriod, hasPlannedBid, isLastBidPointVisited, bidStatus } = realtimeItem;
    const serviceWorks = realtimeItem.serviceWorks ?? [];
    const { bookingComment, isActive, resourceInactivityInfo } = realtimeItem.car;
    const isPlannedBid = bidStatus === bidStatusesEnum.Planned;
    const carnumberCname = cn("carnumber__wrap carnumber__wrap--small", {
        "carnumber__wrap--lightgray": Boolean(bookingComment),
        "carnumber__wrap--gray carnumber__wrap--disabled": !isPlannedBid && !isActive && !bookingComment,
        "carnumber__wrap--green": !isPlannedBid && isActive && hasPlannedBid && !bookingComment,
        "carnumber__wrap--blue":
            !isPlannedBid &&
            isActive &&
            isLastBidPointVisited &&
            !hasPlannedBid &&
            !withoutBidPeriod &&
            !bookingComment,
        "carnumber__wrap--yellow":
            !isPlannedBid &&
            isActive &&
            !isLastBidPointVisited &&
            !hasPlannedBid &&
            !withoutBidPeriod &&
            !bookingComment,
        "carnumber__wrap--red": !isPlannedBid && isActive && withoutBidPeriod && !bookingComment,
    });

    const carPlanningCommentBody = () => {
        const { createdBy, createdAt, comment } = realtimeItem.car.planningComment;
        return (
            <div>
                <div className="font-weight-bold">Комментарий по планированию:</div>
                <div className="pl-3">
                    {createdBy?.displayName && (
                        <div>
                            <strong className="pr-2">Автор:</strong>
                            <span>{createdBy.displayName}</span>
                        </div>
                    )}
                    {createdAt && (
                        <div>
                            <strong className="pr-2">Дата создания:</strong>
                            <span>{dateTimeToView(createdAt, "DD.MM.YYYY HH:mm (Z)")}</span>
                        </div>
                    )}
                    {comment && (
                        <div>
                            <strong className="pr-2">Комментарий:</strong>
                            <span>{comment}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const overlay = (
        <>
            {!isActive && (
                <div className="alert alert-dark px-2 py-1 mb-1">
                    <h6>Машина не активна</h6>
                    <div>
                        <strong>Период:</strong> {dateTimeToView(resourceInactivityInfo.inactiveSince)} &mdash;{" "}
                        {dateTimeToView(resourceInactivityInfo.inactiveUntil)}
                    </div>
                    <div>
                        <strong>Причина:</strong> {resourceInactivityInfo.inactivityReason}
                    </div>
                </div>
            )}
            <div className="pb-2">
                {hasPlannedBid ? (
                    <span className="badge badge-success">Запланирована заявка по приезду в город</span>
                ) : withoutBidPeriod ? (
                    <span className="badge badge-danger">Простаивает в городе без заявки</span>
                ) : (
                    <span className="badge badge-warning">Не запланирована заявка по приезду в город</span>
                )}
            </div>
            {realtimeItem.logist && (
                <div>
                    <strong>Логист:</strong> {lastNameWithInitials(realtimeItem.logist)}
                </div>
            )}
            {realtimeItem.responsible && (
                <div>
                    <strong>Ответственный:</strong> {lastNameWithInitials(realtimeItem.responsible)}
                </div>
            )}
            {realtimeItem.carGroups?.length > 0 && (
                <div>
                    <strong>Логистические группы:</strong>
                    <ul className="mb-0">
                        {realtimeItem.carGroups.map((item) => (
                            <li key={item.id}>{item.name}</li>
                        ))}
                    </ul>
                </div>
            )}
            {realtimeItem.counterparty && (
                <div>
                    <strong>Контрагент:</strong> {realtimeItem.counterparty.name}
                </div>
            )}
            {realtimeItem.car.planningComment && carPlanningCommentBody()}
            {realtimeItem.carDistanceFromLastPoint && (
                <div>
                    <b className="pr-2">Расстояние от последней точки заявки:</b>
                    <span className="badge badge-danger mr-2">
                        {formatNumber(realtimeItem.carDistanceFromLastPoint)} км
                    </span>
                </div>
            )}
            {realtimeItem.routeDeviationDistance && (
                <div>
                    <b className="pr-2">Отклонение от маршрута:</b>
                    <span className="badge badge-warning mr-2">
                        {formatNumber(realtimeItem.routeDeviationDistance / 1000)} км
                    </span>
                </div>
            )}
        </>
    );
    const dropdProps = {
        item: {
            ...realtimeItem,
            carId: realtimeItem.car.id,
            carPlanningComment: realtimeItem.car.planningComment,
            bookingComment: realtimeItem.car.bookingComment,
            number: realtimeItem.car.number,
        },
        uncontrolled: true,
        ...options,
    };

    const openExternalDistributionBid = () => {
        ymEvent("ExternalDisBids");
        return modal({
            name: "externalDistributionBid",
            title: "Информация по внешнему заказу",
            windowMod: "wide",
            bidId: realtimeItem.bidId,
            carId: realtimeItem.car.id,
            organization,
        });
    };
    const leaveDateOverlay = useMemo(() => {
        return (
            <div>
                <div className="font-weight-bold">Расчетное время выезда</div>
                {realtimeItem.estimationLogist && (
                    <div>
                        Автор: <span className="text-muted">{lastNameWithInitials(realtimeItem.estimationLogist)}</span>
                    </div>
                )}
                {realtimeItem.estimationLogistUpdatedAt && (
                    <div>
                        Дата изменения:{" "}
                        <span className="text-muted">{dateTimeToView(realtimeItem.estimationLogistUpdatedAt)}</span>
                    </div>
                )}
                {realtimeItem.estimationComment && (
                    <div>
                        Комментарий: <br />
                        <em className="text-muted">{realtimeItem.estimationComment}</em>
                    </div>
                )}
            </div>
        );
    }, [realtimeItem.estimationComment, realtimeItem.estimationLogist, realtimeItem.estimationLogistUpdatedAt]);

    return (
        <div className="p-1 font-size-12">
            <div className="d-flex">
                <div className="car-planning__group-index">{index}.</div>
                <div className="flex-1">
                    <div className="d-flex gap-2">
                        <div>
                            <Popover isOpen={openedDropd} positions="right" content={<CarDropd {...dropdProps} />}>
                                <div className="d-inline-block">
                                    <Tooltip overlay={overlay} overlayClassName="car-planning__tooltip" placement="top">
                                        <div className={carnumberCname} onClick={openDropd}>
                                            {isValidCarNumber(realtimeItem.car.number) ? (
                                                <>
                                                    <div className="carnumber__number">
                                                        {carNumber.number.letters[0]}&nbsp;{carNumber.number.number}
                                                        &nbsp;
                                                        {carNumber.number.letters[1]}
                                                    </div>
                                                    <div className="carnumber__region">{carNumber.region}</div>
                                                </>
                                            ) : (
                                                <div className="carnumber__number carnumber__number--without-region">
                                                    {realtimeItem.car.number}
                                                </div>
                                            )}
                                        </div>
                                    </Tooltip>
                                </div>
                            </Popover>
                            {(realtimeItem.manualEstimatedLeaveDate || realtimeItem.estimatedLeaveDate) && (
                                <>
                                    <Tooltip
                                        overlay={leaveDateOverlay}
                                        overlayClassName="car-planning__tooltip"
                                        placement="top"
                                    >
                                        <small className="nowrap-text">
                                            {dateTimeToView(
                                                realtimeItem.manualEstimatedLeaveDate ||
                                                    realtimeItem.estimatedLeaveDate,
                                                "DD.MM.YYYY HH:mm (Z)",
                                            )}
                                        </small>
                                    </Tooltip>
                                    {showComment && realtimeItem.estimationComment && (
                                        <Tooltip
                                            overlay={<em className="text-muted">{realtimeItem.estimationComment}</em>}
                                            overlayClassName="car-planning__tooltip"
                                        >
                                            <div className="kek">
                                                <em className="text-muted">
                                                    {truncateString(realtimeItem.estimationComment, 30)}
                                                </em>
                                            </div>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            {(realtimeItem.car.bookingComment ||
                                realtimeItem.car.needsMaintenanceComment ||
                                realtimeItem.isCarOnServicePoint ||
                                serviceWorks.length > 0) && (
                                <div className="d-inline pl-1">
                                    {realtimeItem.car.bookingComment && (
                                        <Tooltip
                                            overlayClassName="planning-table__body--tooltip"
                                            placement="bottom"
                                            overlay={realtimeItem.car.bookingComment}
                                        >
                                            <i className="pr-2 icon-uEA81-lock text-muted inline-icon" />
                                        </Tooltip>
                                    )}
                                    {realtimeItem.car.needsMaintenanceComment && (
                                        <Tooltip
                                            overlayClassName="planning-table__body--tooltip"
                                            placement="bottom"
                                            overlay={<WithoutDriver title={realtimeItem.car.needsMaintenanceComment} />}
                                        >
                                            <i
                                                className={cn("pr-2 icon-uEA97-wrench inline-icon", {
                                                    "text-muted": !realtimeItem.car.needsUrgentMaintenance,
                                                    "text-danger": realtimeItem.car.needsUrgentMaintenance,
                                                })}
                                            />
                                        </Tooltip>
                                    )}
                                    {realtimeItem.isCarOnServicePoint && (
                                        <Tooltip
                                            overlayClassName="planning-table__body--tooltip"
                                            placement="bottom"
                                            overlay="На сервисной точке"
                                        >
                                            <i className="pr-2 icon-uEA97-wrench text-success inline-icon" />
                                        </Tooltip>
                                    )}
                                    {serviceWorks.length > 0 && (
                                        <Tooltip
                                            overlayClassName="planning-table__body--tooltip"
                                            placement="bottom"
                                            overlay="Сервисные работы"
                                        >
                                            <i
                                                className="pr-2 icon-uEA97-wrench text-muted inline-icon cursor-pointer"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    modal({
                                                        name: "default",
                                                        title: "Сервисные работы",
                                                        windowMod: "wide",
                                                        CustomBody: ServiceWorksPlanning,
                                                        bodyData: { serviceWorks },
                                                    });
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                            <div className="pt-1 car-planning__vehicle-desc">
                                {realtimeItem.driver && (
                                    <div className="d-flex">
                                        <div className="text-muted font-weight-bold">
                                            {lastNameWithInitials(realtimeItem.driver)}
                                        </div>
                                        {realtimeItem.driver.needsShiftChangeComment && (
                                            <Tooltip
                                                overlayClassName="planning-table__body--tooltip"
                                                placement="bottom"
                                                overlay={
                                                    <WithoutDriver
                                                        title={realtimeItem.driver.needsShiftChangeComment}
                                                    />
                                                }
                                            >
                                                <i
                                                    className={cn("icon-uEAB2-people-arrows pl-1 inline-icon", {
                                                        "text-muted": !realtimeItem.driver.needsUrgentShiftChange,
                                                        "text-danger": realtimeItem.driver.needsUrgentShiftChange,
                                                    })}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                {realtimeItem.secondaryDriver && (
                                    <div className="text-muted font-weight-bold">
                                        {lastNameWithInitials(realtimeItem.secondaryDriver)}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            {realtimeItem.trailer && (
                                <div>
                                    {isValidTrailerNumber(realtimeItem.trailer?.number) ? (
                                        <div className="carnumber__wrap carnumber__wrap--small">
                                            <div className="carnumber__number">
                                                {trailerNumber.number.letters[0]}&nbsp;{trailerNumber.number.number}
                                            </div>
                                            <div className="carnumber__region">
                                                {trailerNumber.region || <span>&nbsp;</span>}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="carnumber__wrap carnumber__wrap--small">
                                            <div className="carnumber__number carnumber__number--without-region">
                                                {realtimeItem.trailer?.number}
                                            </div>
                                        </div>
                                    )}
                                    <div className="pt-1 car-planning__trailer-desc">
                                        <b className="text-muted">
                                            {trailerTypes[realtimeItem.trailer?.typeId]?.displayName}
                                        </b>
                                    </div>
                                </div>
                            )}
                            {forwardingEnabled && realtimeItem.carExternalOutput != null && (
                                <Tooltip
                                    overlayClassName="planning-table__body--tooltip"
                                    overlay={`Есть подходящий внешний заказ от ${formatNumber(realtimeItem.carExternalOutput)} ${
                                        organization?.currencyData?.name
                                    }/км`}
                                    placement="bottom"
                                >
                                    <div className="car-planning__external-bid">
                                        <i
                                            onClick={openExternalDistributionBid}
                                            className="icon-uEAB5-truck-cost planning-table__body--external-bid-icon-small text-danger"
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <div className="font-size-14 d-inline-block">
                        <Tooltip overlay="Общий пробег с 1го числа текущего месяца" placement="bottom">
                            <span className={cn("badge badge-pill badge-secondary mr-2")}>
                                {formatNumber(realtimeItem.overallMileage)} км
                            </span>
                        </Tooltip>
                        <Tooltip
                            overlay="Выработка с 1го числа текущего месяца. (ВАЛ / общий пробег)"
                            placement="bottom"
                        >
                            <span className={cn("badge badge-pill badge-secondary")}>
                                {formatNumber(realtimeItem.output)} {organization?.currencyData?.name}/км
                            </span>
                        </Tooltip>
                    </div>
                    <div className="d-flex gap-1">
                        {!!realtimeItem.carGroups?.length && (
                            <Tooltip overlay="Логистическая группа" placement="bottom">
                                <div className="font-size-12 pt-1 d-inline-block">
                                    {realtimeItem.carGroups.map((group) => (
                                        <div key={group.id} className="badge badge-primary">
                                            {group.name}
                                        </div>
                                    ))}
                                </div>
                            </Tooltip>
                        )}
                        {realtimeItem.counterparty && (
                            <Tooltip overlay="Контрагент" placement="bottom">
                                <div className="font-size-12 pt-1 d-inline-block">
                                    <div className="badge badge-info">{realtimeItem.counterparty.name}</div>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
